import { Close } from "@mui/icons-material";
import { Chip, Grid, IconButton, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import { DateCalendar, MobileDatePicker } from "@mui/x-date-pickers";
import { sub, getYear, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns'
import { useTranslation } from "react-i18next";

interface IProps {
    fromDate?: Date,
    handleFromDate: (d?: Date) => void,
    toDate?: Date,
    handleToDate: (d?: Date) => void,
    name: string,
    handleClose: () => void,
    mobile?: boolean,
}

const DateRangePicker = (props: IProps): JSX.Element => {

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    function thisWeek(){
        props.handleFromDate(startOfWeek(new Date(), {weekStartsOn:1}));
        props.handleToDate(endOfWeek(new Date(), { weekStartsOn: 1 }));
    }
    function thisMonth(){
        props.handleFromDate(startOfMonth(new Date()));
        props.handleToDate(endOfMonth(new Date()));
    }
    function thisQuarter(){
        props.handleFromDate(startOfQuarter(new Date()));
        props.handleToDate(endOfQuarter(new Date()));
    }
    function thisYear(){
        props.handleFromDate(startOfYear(new Date()));
        props.handleToDate(endOfYear(new Date()));
    }
    function lastWeek() {
        props.handleFromDate(startOfWeek(sub(new Date(), {weeks:1}), { weekStartsOn: 1 }));
        props.handleToDate(endOfWeek(sub(new Date(), { weeks: 1 }), { weekStartsOn: 1 }));
    }
    function lastMonth() {
        props.handleFromDate(startOfMonth(sub(new Date(), { months: 1 })));
        props.handleToDate(endOfMonth(sub(new Date(), { months: 1 })));
    }
    function lastQuarter() {
        props.handleFromDate(startOfQuarter(sub(new Date(), { months: 3 })));
        props.handleToDate(endOfQuarter(sub(new Date(), { months: 3 })));
    }
    function lastYear() {
        props.handleFromDate(startOfYear(sub(new Date(), { years: 1 })));
        props.handleToDate(endOfYear(sub(new Date(), { years: 1 })));
    }
    function quarter(startMonth: number) {
        props.handleFromDate(new Date(getYear(new Date()), startMonth, 1));
        props.handleToDate(endOfQuarter(new Date(getYear(new Date()), startMonth, 1)));
    }
    function term(month: number) {
        props.handleFromDate(new Date(getYear(new Date()), month, 1));
        props.handleToDate(endOfMonth(new Date(getYear(new Date()), month+1, 1)));
    }
    function month(month: number) {
        props.handleFromDate(new Date(getYear(new Date()), month, 1));
        props.handleToDate(endOfMonth(new Date(getYear(new Date()), month, 1)));
    }
    function clearFromDate() {
        props.handleFromDate(undefined);
    }
    function clearToDate() {
        props.handleToDate(undefined);
    }
    function clearFromAndToDate() {
        props.handleFromDate(undefined);
        props.handleToDate(undefined);
    }

    function isMobile() {
        if (props.mobile) {
            return <Stack direction="row" spacing={theme.spacing(2)} margin={theme.spacing(2)}>
                <MobileDatePicker
                    label={t('dateSearch.from')}
                    value={props.fromDate}
                    maxDate={props.toDate}
                    onChange={(newDate) => newDate ? props.handleFromDate(newDate) : props.handleToDate(undefined)} />
                <MobileDatePicker
                    label={t('dateSearch.to')}
                    value={props.toDate}
                    minDate={props.fromDate}
                    onChange={(newDate) => newDate ? props.handleToDate(newDate) : props.handleToDate(undefined)} />
            </Stack>
        } else {
            return <Grid container>
                <Grid item direction="column" xs={6}>
                    <Typography variant="button" margin={theme.spacing(2)}>{t('dateSearch.from')}:</Typography>
                    <DateCalendar value={props.fromDate} maxDate={props.toDate} onChange={(newDate) => newDate ? props.handleFromDate(newDate) : props.handleToDate(undefined)} />
                </Grid>
                <Grid item xs={6} direction="column">
                    <Typography variant="button" margin={theme.spacing(2)}>{t('dateSearch.to')}:</Typography>
                    <DateCalendar value={props.toDate} minDate={props.fromDate} onChange={(newDate) => newDate ? props.handleToDate(newDate) : props.handleToDate(undefined)} />
                </Grid>
            </Grid>
        }
    }

    return <Grid container direction="column" margin={theme.spacing(1)}>
        <Grid container justifyContent="space-between" alignItems="center" paddingLeft={theme.spacing(1)} paddingRight={theme.spacing(1)}>
            <Grid item><Typography variant="h6">{props.name}</Typography></Grid>
            <Grid item><IconButton onClick={props.handleClose}><Close /></IconButton></Grid>
        </Grid>
        {isMobile()}
        <Grid container direction="column" marginTop={props.mobile ? theme.spacing(0) : theme.spacing(-5)}>
            <Grid container margin={theme.spacing(1)} spacing={1}>
                <Grid item xs={12}><Typography variant="button">{t('dateSearch.shortkeys')}:</Typography></Grid>
                <Grid item><Chip color="info" label={t('dateSearch.clearFrom')} onClick={() => clearFromDate()} /></Grid>
                <Grid item><Chip color="info" label={t('dateSearch.clearTo')} onClick={() => clearToDate()} /></Grid>
                <Grid item><Chip color="info" label={t('dateSearch.clearAll')} onClick={() => clearFromAndToDate()} /></Grid>
            </Grid>
            <Grid container margin={theme.spacing(1)} spacing={1}>
                <Grid item><Chip color="warning" label={t('dateSearch.thisWeek')} onClick={() => thisWeek()} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.thisMonth')} onClick={() => thisMonth()} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.thisQuarter')} onClick={() => thisQuarter()} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.thisYear')} onClick={() => thisYear()} /></Grid>
            </Grid>
            <Grid container margin={theme.spacing(1)} spacing={1}>
                <Grid item><Chip color="warning" label={t('dateSearch.lastWeek')} onClick={() => lastWeek()} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.lastMonth')} onClick={() => lastMonth()} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.lastQuarter')} onClick={() => lastQuarter()} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.lastYear')} onClick={() => lastYear()} /></Grid>
            </Grid>
            <Grid container margin={theme.spacing(1)} spacing={1}>
                <Grid item><Chip color="warning" label={t('dateSearch.quarter1')} onClick={() => quarter(0)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.quarter2')} onClick={() => quarter(3)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.quarter3')} onClick={() => quarter(6)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.quarter4')} onClick={() => quarter(9)} /></Grid>
            </Grid>
            <Grid container margin={theme.spacing(1)} spacing={1}>
                <Grid item><Chip color="warning" label={t('dateSearch.term1')} onClick={() => term(0)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.term2')} onClick={() => term(2)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.term3')} onClick={() => term(4)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.term4')} onClick={() => term(6)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.term5')} onClick={() => term(8)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.term6')} onClick={() => term(10)} /></Grid>
            </Grid>
            <Grid container margin={theme.spacing(1)} spacing={1}>
                <Grid item><Chip color="warning" label={t('dateSearch.jan')} onClick={() => month(0)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.feb')} onClick={() => month(1)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.march')} onClick={() => month(2)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.apr')} onClick={() => month(3)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.may')} onClick={() => month(4)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.june')} onClick={() => month(5)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.july')} onClick={() => month(6)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.aug')} onClick={() => month(7)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.sep')} onClick={() => month(8)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.oct')} onClick={() => month(9)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.nov')} onClick={() => month(10)} /></Grid>
                <Grid item><Chip color="warning" label={t('dateSearch.dec')} onClick={() => month(11)} /></Grid>
            </Grid>
        </Grid>
    </Grid>

}
export default DateRangePicker;